import Immutable from 'seamless-immutable';
import { snakeCase } from 'lodash';
import { takeLatest, takeEvery, ForkEffect } from 'redux-saga/effects';
import { createReducer, HandlersMap } from './utils';
import { PENDING } from './constants';

// Interfaces de configuración para los Efectos (Sagas)
export interface EffectConfig {
  type?: 'all' | 'latest' | string;
  callback: (action: any, actionCreators: any) => Generator<any, any, any>;
}

// Interfaz para el objeto de configuración que recibe createModule
export interface ModuleConfig<S> {
  name: string;
  state: S;
  handlers: HandlersMap<S>;
  effects?: {
    [key: string]: EffectConfig;
  };
}

// Interfaz para el módulo final generado y tipado
export interface GeneratedModule<S> {
  name: string;
  initialState: Immutable.Immutable<S>;
  actionCreators: Record<string, Function>;
  actionsTypes: Record<string, string>;
  actionHandlers: HandlersMap<S>;
  reducer: Function;
  effects: Record<string, Function>;
  sagas: ForkEffect[];
}

/**
 * Alternative to create a module
 */
export const createModule = <S>(config: ModuleConfig<S>): GeneratedModule<S> => {
  // Initial State estructurado con seamless-immutable
  const initialState = Immutable(config.state);
  let actionCreators: Record<string, Function> = {};
  let actionsTypes: Record<string, string> = {};
  let actionHandlers: HandlersMap<S> = {};
  let effects: Record<string, Function> = {};
  let sagas: ForkEffect[] = [];

  Object.keys(config.handlers).map(actionName => {
    // Action Type
    const actionType = `@${config.name}/${snakeCase(actionName).toUpperCase()}`;
    actionsTypes[actionName] = actionType;

    let onlyHandler = (name: string): boolean => {
      let isActionType = name.indexOf('@') > -1;
      let isValidName  = name.indexOf('/') === -1;

      return (isActionType || !isValidName);
    };

    // Action Creator
    if (!onlyHandler(actionName)) {
      if (config.effects && config.effects[actionName]) {
        actionCreators[actionName] = (data: any, status: string) => ({
          type: actionType,
          payload: data,
          status,
        });
      } else {
        actionCreators[actionName] = (data: any) => ({
          type: actionType,
          payload: data,
        });
      }
    }

    // Action Handler for reducer
    if (onlyHandler(actionName)) {
      actionHandlers[actionName] = config.handlers[actionName];
    } else {
      actionHandlers[actionType] = config.handlers[actionName];
    }

    // Effects ligados a Handlers
    if (config.effects && config.effects[actionName]) {
      let callback = config.effects[actionName].callback;
      
      const effect = config.effects[actionName].type === 'all' ? takeEvery : takeLatest;

      sagas.push(
        effect((action: any) => {
          return action.type === actionType && (!action.status || action.status === PENDING);
        }, callback, actionCreators)
      );

      effects[actionName] = callback;
    }
  });

  // We set the "free" effects. This are effects that don't match with the handlers in the model.
  if (config.effects) {
    Object.keys(config.effects).map(eff => {
      let isActionType = eff.indexOf('@') > -1;
      let isValidName  = eff.indexOf('/') === -1;

      let isActionName = (isActionType || !isValidName);

      // If the action doesn't exist and the effect its an action name (@model/action)
      if (!config.handlers[eff] && isActionName && config.effects) {
        let callback = config.effects[eff].callback;
        const effect = config.effects[eff].type === 'all' ? takeEvery : takeLatest;

        sagas.push(
          effect((action: any) => {
            return action.type === eff && (!action.status || action.status === PENDING);
          }, callback, actionCreators)
        );

        // Corregido el bug asignando correctamente la llave 'eff' del efecto desacoplado
        effects[eff] = callback;
      }
    });
  }

  const reducer = createReducer(
    initialState,
    actionHandlers
  );

  return {
    name: config.name,
    initialState,
    actionCreators,
    actionsTypes,
    actionHandlers,
    reducer,
    effects,
    sagas,
  };
};